import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress,
  IconButton, Autocomplete, Box, Checkbox, FormControlLabel, Typography, Divider,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createBom, updateBom, getBom, type BomListItem, type SaveBomPayload } from '../../api/productie'
import { listArticles, listUnits, getArticle } from '../../api/nomenclatoare'
import { listClients } from '../../api/clienti'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({
  componentArticleId: z.string().min(1, 'Selectați o componentă'),
  quantityPerThousand: z.string().min(1, 'Câmp obligatoriu'),
  unitOfMeasureId: z.string().min(1, 'Selectați o UM'),
  tolerancePercent: z.string().optional(),
  isRequired: z.boolean(),
  notes: z.string().optional(),
})
const schema = z.object({
  productArticleId: z.string().min(1, 'Selectați produsul finit'),
  clientId: z.string().nullable(),
  validFrom: z.string().min(1, 'Câmp obligatoriu'),
  validTo: z.string().optional(),
  thermoformingLossPercent: z.string().optional(),
  cuttingLossPercent: z.string().optional(),
  scrapLossPercent: z.string().optional(),
  conversionCostPerThousand: z.string().optional(),
  processTemperatureC: z.string().optional(),
  cycleTimeSeconds: z.string().optional(),
  pressureBar: z.string().optional(),
  wallThicknessMm: z.string().optional(),
  qualityToleranceNotes: z.string().optional(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o componentă'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  productArticleId: '', clientId: null, validFrom: new Date().toISOString().slice(0, 10), validTo: '',
  thermoformingLossPercent: '', cuttingLossPercent: '', scrapLossPercent: '', conversionCostPerThousand: '',
  processTemperatureC: '', cycleTimeSeconds: '', pressureBar: '', wallThicknessMm: '', qualityToleranceNotes: '',
  lines: [],
}

export default function BomFormDialog({ target, onClose }: { target: BomListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['productie', 'retetar', editing ? (target as BomListItem).id : null],
    queryFn: () => getBom((target as BomListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const watched = useWatch({ control })

  useEffect(() => {
    if (editing && detail) {
      reset({
        productArticleId: detail.productArticleId, clientId: detail.clientId, validFrom: detail.validFrom, validTo: detail.validTo ?? '',
        thermoformingLossPercent: detail.thermoformingLossPercent != null ? String(detail.thermoformingLossPercent) : '',
        cuttingLossPercent: detail.cuttingLossPercent != null ? String(detail.cuttingLossPercent) : '',
        scrapLossPercent: detail.scrapLossPercent != null ? String(detail.scrapLossPercent) : '',
        conversionCostPerThousand: detail.conversionCostPerThousand != null ? String(detail.conversionCostPerThousand) : '',
        processTemperatureC: detail.processTemperatureC != null ? String(detail.processTemperatureC) : '',
        cycleTimeSeconds: detail.cycleTimeSeconds != null ? String(detail.cycleTimeSeconds) : '',
        pressureBar: detail.pressureBar != null ? String(detail.pressureBar) : '',
        wallThicknessMm: detail.wallThicknessMm != null ? String(detail.wallThicknessMm) : '',
        qualityToleranceNotes: detail.qualityToleranceNotes ?? '',
        lines: detail.lines.map((l) => ({
          componentArticleId: l.componentArticleId, quantityPerThousand: String(l.quantityPerThousand), unitOfMeasureId: l.unitOfMeasureId,
          tolerancePercent: l.tolerancePercent != null ? String(l.tolerancePercent) : '', isRequired: l.isRequired, notes: l.notes ?? '',
        })),
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const addFoliePrompted = async () => {
    if (!watched.productArticleId) return
    const product = await getArticle(watched.productArticleId)
    const totalLoss = (Number(watched.thermoformingLossPercent) || 0) + (Number(watched.cuttingLossPercent) || 0) + (Number(watched.scrapLossPercent) || 0)
    const suggestedQuantity = product.unitWeightG != null ? product.unitWeightG * (1 + totalLoss / 100) : 0
    lines.append({ componentArticleId: '', quantityPerThousand: suggestedQuantity ? suggestedQuantity.toFixed(3) : '', unitOfMeasureId: '', tolerancePercent: '', isRequired: true, notes: '' })
  }

  // Cost calculat live, la fel ca la citirea de pe backend (FR-BOM-009/010) — folosește standardCost-ul curent din nomenclator.
  const componentsCost = (watched.lines ?? []).reduce((sum, l) => {
    const article = articles?.find((a) => a.id === l?.componentArticleId)
    const qty = Number(l?.quantityPerThousand) || 0
    return sum + qty * (article?.standardCost ?? 0)
  }, 0)
  const totalCost = componentsCost + (Number(watched.conversionCostPerThousand) || 0)

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveBomPayload = {
        productArticleId: values.productArticleId, clientId: values.clientId || null,
        validFrom: values.validFrom, validTo: values.validTo || null,
        thermoformingLossPercent: values.thermoformingLossPercent ? Number(values.thermoformingLossPercent) : null,
        cuttingLossPercent: values.cuttingLossPercent ? Number(values.cuttingLossPercent) : null,
        scrapLossPercent: values.scrapLossPercent ? Number(values.scrapLossPercent) : null,
        conversionCostPerThousand: values.conversionCostPerThousand ? Number(values.conversionCostPerThousand) : null,
        processTemperatureC: values.processTemperatureC ? Number(values.processTemperatureC) : null,
        cycleTimeSeconds: values.cycleTimeSeconds ? Number(values.cycleTimeSeconds) : null,
        pressureBar: values.pressureBar ? Number(values.pressureBar) : null,
        wallThicknessMm: values.wallThicknessMm ? Number(values.wallThicknessMm) : null,
        qualityToleranceNotes: values.qualityToleranceNotes || null,
        lines: values.lines.map((l) => ({
          componentArticleId: l.componentArticleId, quantityPerThousand: Number(l.quantityPerThousand), unitOfMeasureId: l.unitOfMeasureId,
          tolerancePercent: l.tolerancePercent ? Number(l.tolerancePercent) : null, isRequired: l.isRequired, notes: l.notes || null,
        })),
      }
      if (editing) return updateBom((target as BomListItem).id, payload)
      return createBom(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productie', 'retetar'] })
      onClose()
    },
  })

  const readOnly = editing && detail && detail.status !== 'Draft'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? `Rețetă ${detail?.code} v${detail?.version}` : 'Rețetă nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {readOnly && <Alert severity="info">Doar rețetele în ciornă pot fi editate — pentru o schimbare, creați o versiune nouă.</Alert>}
            <Stack direction="row" spacing={2}>
              <Controller name="productArticleId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} disabled={!!readOnly} options={articles?.filter((a) => a.type !== 'Set').map((a) => a.id) ?? []}
                  getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Produs finit" error={!!errors.productArticleId} helperText={errors.productArticleId?.message} />}
                />
              )} />
              <Controller name="clientId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ width: 260 }} disabled={!!readOnly} options={clients?.map((c) => c.id) ?? []}
                  getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
                  value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                  renderInput={(params) => <TextField {...params} label="Client (opțional — rețetă specifică)" />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="validFrom" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="date" label="Valabil de la" sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.validFrom} helperText={errors.validFrom?.message} />
              )} />
              <Controller name="validTo" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="date" label="Valabil până la (opțional)" sx={{ width: 200 }} slotProps={{ inputLabel: { shrink: true } }} />
              )} />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Pierderi tehnologice normate (FR-BOM-005)</Typography>
            <Stack direction="row" spacing={2}>
              <Controller name="thermoformingLossPercent" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Pierdere termoformare %" sx={{ width: 180 }} />
              )} />
              <Controller name="cuttingLossPercent" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Pierdere tăiere %" sx={{ width: 160 }} />
              )} />
              <Controller name="scrapLossPercent" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Rebut %" sx={{ width: 140 }} />
              )} />
              <Controller name="conversionCostPerThousand" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Cost conversie /1000" sx={{ width: 180 }} />
              )} />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Fișă tehnologică (FR-BOM-011, opțional)</Typography>
            <Stack direction="row" spacing={2}>
              <Controller name="processTemperatureC" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Temperatură (°C)" sx={{ width: 150 }} />
              )} />
              <Controller name="cycleTimeSeconds" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Timp ciclu (s)" sx={{ width: 150 }} />
              )} />
              <Controller name="pressureBar" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Presiune (bar)" sx={{ width: 150 }} />
              )} />
              <Controller name="wallThicknessMm" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Grosime perete (mm)" sx={{ width: 170 }} />
              )} />
            </Stack>
            <Controller name="qualityToleranceNotes" control={control} render={({ field }) => (
              <TextField {...field} disabled={!!readOnly} label="Toleranțe de calitate (note)" fullWidth multiline minRows={2} />
            )} />

            <Divider />
            <Typography variant="subtitle2">Componente</Typography>
            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`lines.${i}.componentArticleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} disabled={!!readOnly} options={articles?.map((a) => a.id) ?? []}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Componentă" error={!!errors.lines?.[i]?.componentArticleId}
                      helperText={errors.lines?.[i]?.componentArticleId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.quantityPerThousand`} control={control} render={({ field }) => (
                  <TextField {...field} disabled={!!readOnly} type="number" label="Cant. / 1.000 buc" sx={{ width: 150 }} error={!!errors.lines?.[i]?.quantityPerThousand}
                    helperText={errors.lines?.[i]?.quantityPerThousand?.message} />
                )} />
                <Controller name={`lines.${i}.unitOfMeasureId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ width: 110 }} disabled={!!readOnly} options={units?.map((u) => u.id) ?? []}
                    getOptionLabel={(id) => units?.find((u) => u.id === id)?.code ?? id}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="UM" error={!!errors.lines?.[i]?.unitOfMeasureId}
                      helperText={errors.lines?.[i]?.unitOfMeasureId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.tolerancePercent`} control={control} render={({ field }) => (
                  <TextField {...field} disabled={!!readOnly} type="number" label="Toleranță %" sx={{ width: 120 }} />
                )} />
                <Controller name={`lines.${i}.isRequired`} control={control} render={({ field }) => (
                  <FormControlLabel sx={{ mt: 0.5 }} control={<Checkbox checked={field.value} disabled={!!readOnly} onChange={(e) => field.onChange(e.target.checked)} />} label="Oblig." />
                )} />
                <Controller name={`lines.${i}.notes`} control={control} render={({ field }) => (
                  <TextField {...field} disabled={!!readOnly} label="Observații" sx={{ flexGrow: 1 }} />
                )} />
                {!readOnly && <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>}
              </Stack>
            ))}
            {!readOnly && (
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<Add />}
                  onClick={() => lines.append({ componentArticleId: '', quantityPerThousand: '', unitOfMeasureId: '', tolerancePercent: '', isRequired: true, notes: '' })}>
                  Adaugă componentă
                </Button>
                <Button size="small" startIcon={<Add />} disabled={!watched.productArticleId} onClick={() => void addFoliePrompted()}>
                  Adaugă folie (cantitate sugerată din greutatea produsului)
                </Button>
              </Stack>
            )}

            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">Cost componente / 1.000 buc: <b>{componentsCost.toFixed(4).replace('.', ',')}</b></Typography>
              <Typography variant="body2">Cost total (+ conversie) / 1.000 buc: <b>{totalCost.toFixed(4).replace('.', ',')}</b></Typography>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          {!readOnly && (
            <Button type="submit" variant="contained" disabled={mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              Salvează
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}
